import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Grid,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
} from '@mui/material';
import { ArrowBack, ShoppingCart, Person, LocalPharmacy, Receipt, Print } from '@mui/icons-material';
import apiClient from '../lib/axios';
import { PrintablePrescription } from '../components/PrintablePrescription';

interface OrderItem {
  id: string;
  presentationId: string;
  quantity: number;
  dosage: string;
  instructions?: string;
  price: number;
  presentation: {
    id: string;
    form: string;
    concentration: string;
    product: {
      id: string;
      brandName: string;
      activePrinciple: {
        name: string;
      };
    };
  };
}

interface OrderDetail {
  id: string;
  orderNumber: string;
  status: string;
  patientName: string;
  patientPhone: string;
  patientEmail?: string;
  patientDateOfBirth?: string;
  notes?: string;
  totalAmount: number;
  commissionRate: number;
  commissionAmount: number;
  notificationsEnabled: boolean;
  createdAt: string;
  updatedAt: string;
  doctor: {
    id: string;
    firstName: string;
    lastName: string;
    licenseNumber: string;
    clinicName?: string;
    rncTaxId?: string;
    clinicAddress?: string;
    cmdNumber?: string;
    specialty?: {
      id: string;
      name: string;
    };
  };
  pharmacyChain?: {
    id: string;
    name: string;
    logoUrl?: string;
  };
  items: OrderItem[];
}

const statusColors: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'> = {
  PENDING: 'warning',
  IN_PROGRESS: 'info',
  READY_FOR_PICKUP: 'primary',
  COMPLETED: 'success',
  CANCELLED: 'error',
};

const getStatusLabel = (status: string, t: (key: string) => string): string => {
  const labels: Record<string, string> = {
    PENDING: t('status.pending'),
    IN_PROGRESS: t('status.inProgress'),
    READY_FOR_PICKUP: t('status.readyForPickup'),
    COMPLETED: t('status.completed'),
    CANCELLED: t('status.cancelled'),
  };
  return labels[status] || status;
};

export const OrderDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const locationState = location.state as { templateName?: string; elapsedTime?: number; triggerPrint?: boolean } | null;
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const printRef = useRef<HTMLDivElement>(null);

  const { data: order, isLoading, error } = useQuery({
    queryKey: ['orders', id],
    queryFn: async () => {
      const { data } = await apiClient.get<OrderDetail>(`/orders/${id}`);
      return data;
    },
    enabled: !!id,
  });

  // Print prescription function
  const handlePrint = () => {
    window.print();
  };

  // Show success message if coming from template-based creation
  useEffect(() => {
    if (locationState?.templateName) {
      setShowSuccessMessage(true);
      // Auto-hide after 10 seconds
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [locationState]);

  // Auto-trigger print if requested
  useEffect(() => {
    if (locationState?.triggerPrint && order) {
      // Delay slightly to ensure the component is rendered
      const timer = setTimeout(() => {
        handlePrint();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [locationState?.triggerPrint, order]);

  const updateStatusMutation = useMutation({
    mutationFn: async (status: string) => {
      const { data } = await apiClient.put(`/orders/${id}/status`, { status });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders', id] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !order) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <Alert severity="error">{t('orders.detail.loadError')}</Alert>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/doctor/orders')}
            sx={{ mt: 2 }}
          >
            {t('orders.detail.backToOrders')}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ShoppingCart sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
              <Box>
                <Typography variant="h4" component="h1" fontWeight="bold">
                  {t('orders.detail.orderNumber', { number: order.orderNumber })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('orders.detail.created', { date: new Date(order.createdAt).toLocaleString() })}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate('/doctor/orders')}
            >
              {t('orders.detail.backToOrders')}
            </Button>
          </Box>

          {showSuccessMessage && locationState && (
            <Alert
              severity="success"
              onClose={() => setShowSuccessMessage(false)}
              sx={{ mb: 3 }}
            >
              <Typography variant="body1" fontWeight="bold">
                {locationState.templateName
                  ? t('orders.detail.createdWithTemplate', { template: locationState.templateName })
                  : t('orders.detail.createdSuccess')}
              </Typography>
              {locationState.elapsedTime !== undefined && (
                <Typography variant="body2">
                  {t('orders.detail.createdIn', {
                    minutes: Math.floor(locationState.elapsedTime / 60),
                    seconds: locationState.elapsedTime % 60,
                  })}
                </Typography>
              )}
            </Alert>
          )}

          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('orders.detail.currentStatus')}
                </Typography>
                <Chip
                  label={getStatusLabel(order.status, t)}
                  color={statusColors[order.status] || 'default'}
                  sx={{ mt: 0.5 }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<Print />}
                  onClick={handlePrint}
                >
                  {t('common.print')}
                </Button>
                {order.status !== 'CANCELLED' && order.status !== 'COMPLETED' && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => {
                      if (window.confirm(t('orders.detail.cancelConfirm'))) {
                        updateStatusMutation.mutate('CANCELLED');
                      }
                    }}
                    disabled={updateStatusMutation.isPending}
                  >
                    {updateStatusMutation.isPending ? t('orders.detail.cancelling') : t('orders.detail.cancelOrder')}
                  </Button>
                )}
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Person sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">{t('orders.detail.patientInfo')}</Typography>
                  </Box>
                  <Typography variant="body2" gutterBottom>
                    <strong>{t('orders.detail.name')}:</strong> {order.patientName}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>{t('orders.detail.phone')}:</strong> {order.patientPhone}
                  </Typography>
                  {order.patientEmail && (
                    <Typography variant="body2" gutterBottom>
                      <strong>{t('orders.detail.email')}:</strong> {order.patientEmail}
                    </Typography>
                  )}
                  <Typography variant="body2" gutterBottom>
                    <strong>{t('orders.detail.notifications')}:</strong> {order.notificationsEnabled ? t('common.enabled') : t('common.disabled')}
                  </Typography>
                  {order.notes && (
                    <>
                      <Typography variant="body2" sx={{ mt: 2, fontWeight: 'bold' }}>
                        {t('orders.detail.specialInstructions')}:
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {order.notes}
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LocalPharmacy sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">{t('orders.detail.pharmacyInfo')}</Typography>
                  </Box>
                  <Typography variant="body2" gutterBottom>
                    <strong>{t('orders.detail.name')}:</strong> {order.pharmacyChain?.name || 'N/A'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Receipt sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">{t('orders.detail.orderItems')}</Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('orders.detail.medication')}</TableCell>
                    <TableCell>{t('orders.detail.presentation')}</TableCell>
                    <TableCell>{t('orders.detail.dosage')}</TableCell>
                    <TableCell align="center">{t('orders.detail.quantity')}</TableCell>
                    <TableCell align="right">{t('orders.detail.price')}</TableCell>
                    <TableCell align="right">{t('orders.detail.subtotal')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {order.items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {item.presentation.product.brandName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.presentation.product.activePrinciple.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {item.presentation.form} - {item.presentation.concentration}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{item.dosage}</Typography>
                        {item.instructions && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {item.instructions}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">{item.quantity}</TableCell>
                      <TableCell align="right">
                        ${parseFloat(item.price.toString()).toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        ${(parseFloat(item.price.toString()) * item.quantity).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Box sx={{ minWidth: 300 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">{t('orders.detail.subtotalLabel')}:</Typography>
                <Typography variant="body1">
                  ${parseFloat(order.totalAmount.toString()).toFixed(2)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('orders.detail.commission')} ({parseFloat(order.commissionRate.toString()).toFixed(1)}%):
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ${parseFloat(order.commissionAmount.toString()).toFixed(2)}
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" fontWeight="bold">
                  {t('orders.detail.total')}:
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="primary.main">
                  ${parseFloat(order.totalAmount.toString()).toFixed(2)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Hidden Printable Prescription */}
        <Box sx={{ display: 'none', displayPrint: 'block' }} ref={printRef}>
          <PrintablePrescription
            prescriptionNumber={order.orderNumber}
            doctor={{
              firstName: order.doctor.firstName,
              lastName: order.doctor.lastName,
              licenseNumber: order.doctor.licenseNumber,
              clinicName: order.doctor.clinicName,
              rncTaxId: order.doctor.rncTaxId,
              clinicAddress: order.doctor.clinicAddress,
              cmdNumber: order.doctor.cmdNumber,
              specialty: order.doctor.specialty,
            }}
            patient={{
              name: order.patientName,
              dateOfBirth: order.patientDateOfBirth,
            }}
            items={order.items.map((item) => ({
              id: item.id,
              brandName: item.presentation.product.brandName,
              activePrinciple: item.presentation.product.activePrinciple.name,
              form: item.presentation.form,
              dosage: item.presentation.concentration,
              quantity: item.quantity,
              instructions: item.instructions,
            }))}
            orderDate={order.createdAt}
          />
        </Box>
      </Box>
    </Container>
  );
};
